import { Link } from "react-router-dom";
import { ArrowRight, Compass, ShieldCheck, Globe2, HeartHandshake } from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";

const HERO_IMG =
  "https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&w=1600&q=85";

const PORTRAIT_IMG =
  "https://images.unsplash.com/photo-1769144256227-5185141c3aca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxjYXJnbyUyMHNoaXBwaW5nJTIwcG9ydCUyMGNvbnRhaW5lciUyMGFlcmlhbHxlbnwwfHx8fDE3NzgwMzkxMDJ8MA&ixlib=rb-4.1.0&q=85";

const VALUES = [
  {
    icon: Compass,
    title: "On the ground in Yiwu",
    body:
      "We don't broker from a desk. Our team walks the wholesale market every week — supplier verification, factory audits, real-time price discovery.",
  },
  {
    icon: ShieldCheck,
    title: "Compliance, first",
    body:
      "Customs clearance is where most shipments stall. We pre-clear paperwork in India, UK, Middle East and beyond, so your goods land without surprises.",
  },
  {
    icon: Globe2,
    title: "Door-to-door, end-to-end",
    body:
      "One contract covers sourcing, packaging, freight, customs and last-mile. One number to call. One inbox for updates.",
  },
  {
    icon: HeartHandshake,
    title: "Long-term partners",
    body:
      "Brands like Baybee, Style Union, Dispomate and Orthodent have shipped with us repeatedly because the second order is always smoother than the first.",
  },
];

const TIMELINE = [
  { year: "2010", title: "Founded in Yiwu", body: "Started as a sourcing desk for two import clients in India." },
  { year: "2014", title: "First UK & Middle East lanes", body: "Opened door-to-door corridors with verified customs partners." },
  { year: "2018", title: "Brand-led sourcing", body: "Began onboarding consumer brands needing manufacturing audits + ethical sourcing." },
  { year: "2022", title: "End-to-end logistics", body: "Integrated freight, packaging and last-mile under one operator." },
  { year: "2025", title: "42 countries served", body: "Operating across four continents with a multilingual sourcing team." },
];

export default function AboutPage() {
  return (
    <main data-testid="about-page" className="bg-ink-50">
      <Navigation />

      {/* Hero */}
      <section
        data-testid="about-hero"
        className="relative pt-36 md:pt-44 pb-20 md:pb-28 bg-white border-b border-ink-100 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-6">
              About Emporia World
            </p>
            <h1 className="font-heading font-black text-ink-900 tracking-[-0.035em] leading-[0.92] text-5xl md:text-6xl lg:text-7xl">
              We move trade,<br />
              <span className="text-stroke">quietly.</span>
            </h1>
            <p className="mt-8 max-w-xl text-ink-500 text-base md:text-lg leading-relaxed">
              Emporia World is a trade-operations company headquartered in Yiwu,
              China. For 15+ years we've sourced, shipped and cleared goods for
              brands across India, the UK, the Middle East and beyond — with
              one promise: no chasing, no surprises.
            </p>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] overflow-hidden bg-ink relative">
              <img src={HERO_IMG} alt="Modern building — turnkey project delivery" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/70">
                  HQ · 402, Wealth Mansion
                </div>
                <div className="font-heading font-bold text-2xl mt-2">Yiwu, Zhejiang</div>
              </div>
              <span className="absolute top-6 left-6 w-3 h-3 bg-signal" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-ink text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {[
            { n: "15+", l: "Years in trade" },
            { n: "15+", l: "Countries served" },
            { n: "8K+", l: "Shipments cleared" },
            { n: "9", l: "Trade lanes" },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-heading font-black text-white text-5xl md:text-6xl leading-none tracking-tight">
                {s.n}
              </div>
              <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.22em] text-white/55">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission + Values */}
      <section className="bg-white py-14 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 mb-16 md:mb-20">
            <div className="md:col-span-5">
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
                What we believe
              </p>
              <h2 className="font-heading font-black text-ink-900 tracking-[-0.03em] leading-[0.95] text-4xl md:text-5xl">
                Trade is a system,<br />not a transaction.
              </h2>
            </div>
            <p className="md:col-span-6 md:col-start-7 text-ink-500 text-base md:text-lg leading-relaxed">
              Most exporters hand off the hard parts — customs, packaging, last
              mile — and call themselves an agency. We operate the full chain
              under one roof so a problem at any stage is still our problem.
              That's the only model that scales for our clients.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <article
                  key={v.title}
                  data-testid={`about-value-${v.title.replace(/\s+/g, "-").toLowerCase()}`}
                  className="group p-8 md:p-10 bg-ink-50 border border-ink-100 hover:border-signal hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-ink-100 text-ink-900 group-hover:border-signal group-hover:text-signal transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={1.6} />
                  </div>
                  <h3 className="mt-6 font-heading font-bold text-2xl md:text-[26px] tracking-tight text-ink-900">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-ink-500 text-[15px] leading-relaxed">{v.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-ink-50 py-14 md:py-28 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-end mb-14 md:mb-20">
            <div className="md:col-span-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
                Field record
              </p>
              <h2 className="font-heading font-black text-ink-900 tracking-[-0.03em] leading-[0.95] text-4xl md:text-5xl">
                Fifteen years, on the ground.
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 relative">
              <div className="aspect-[4/3] overflow-hidden bg-ink">
                <img src={PORTRAIT_IMG} alt="Aerial view of port operations" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>

          <ol className="relative border-l-2 border-ink-100 ml-3 md:ml-4">
            {TIMELINE.map((t, i) => (
              <li
                key={t.year}
                data-testid={`timeline-${t.year}`}
                className="relative pl-8 md:pl-12 pb-12 last:pb-0"
              >
                <span className="absolute -left-[9px] top-1.5 w-4 h-4 bg-signal" />
                <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink-500 mb-2">
                  {t.year}
                </div>
                <h3 className="font-heading font-bold text-2xl md:text-3xl tracking-tight text-ink-900">
                  {t.title}
                </h3>
                <p className="mt-3 max-w-2xl text-ink-500 text-[15px] leading-relaxed">{t.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-ink text-white py-14 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <h2 className="font-heading font-black tracking-[-0.03em] leading-[0.95] text-4xl md:text-5xl lg:text-6xl">
              Have a sourcing brief?<br />
              <span className="text-stroke-light">We'll send a real quote.</span>
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/#contact"
              data-testid="about-cta-contact"
              className="group inline-flex items-center gap-3 bg-signal hover:bg-signal-600 text-white px-7 py-4 font-mono text-[12px] uppercase tracking-[0.22em] transition-colors"
            >
              Start a conversation
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
